import { SessionEvents, ParticipantTypes, SessionTypes, DeviceKinds } from "./types.js";
import { Participant } from "./participant.js";
import { VideoDeviceParticipant } from "./video_device_participant.js";
import { WebRTCConnectionWatcher } from "./issue_detector.js";
import { SessionContext } from "./session_context.js";
import { compressPusherPayload, decompressPusherPayload, buildRTCPeerConnectionConfig, applyRemoteAnswer, isEventTargetedAtPeer } from "./shared.js";
import { ViewerTelemetry } from "./viewer_telemetry.js";


/**
 * @typedef {Object} JoinData - see lightfoot webrtc_peer.py for webrtc payload
 * @param {string} result - the id of the room
 * @param {int} device_id - the id of the device
 * @param {DeviceKinds} device_kind - the kind of device (e.g., fixed, mobilecart, kiosk, etc.)
 * @param {string} video_type - channel type??
 * @param {string} channel - channel name??
 * @param {string} peer_id - id of the peer browser user
 * @param {string} sdp - remote sdp of the video device
 * @param {Object[]} ice_candidates - list of ice candidates
 * @param {string} device_version - semver version of the device (from video_device.rb)
 * @param {Object[]} turn_servers - list of turn servers (from video_device.rb)
 * @param {int} meeting_id - the id of the meeting (if applicable - from video_controller.rb)
 * @param {string} meeting_channel - the pusher presence channel name for the meeting (if applicable - from video_controller.rb)
 */

/**
 * WebRTCSession represents a webRTC session of a connection to a video device. It helps
 * manage the connection to the video device, the patient kiosk, and other users.
 * 
 * Flows:
 * - connect to patient device
 * - connect to patient kiosk browser (via patient device request)
 * - connect to other user (via patient device request)
 */
export class WebRTCSession {
  #eventListeners = new Map()
  #videoDeviceParticipant = null
  #videoDeviceKioskParticipant = null
  #peerBrowserParticipants = new Map()
  #webrtcWatcher = null
  #viewerTelemetry = null
  #deviceRtcPeerConnectionConfig = null
  #meetingChannel = null
  #deviceConnected = false
  #devicePatientTrackReceived = false
  #patientMediaReadyEmitted = false
  context = null
  peerId = null

  /**
   * Adds an event callback
   * @param {string} eventType The type of event
   * @param {string} eventCallback A callback
   */
  on(eventType, eventCallback) {
    if (this.#eventListeners.has(eventType)) {
      console.error(`WebRTCSession: listener already registered for event '${eventType}'. Replacing existing listener.`);
    }
    this.#eventListeners.set(eventType, eventCallback);
  }

  emit(eventType, ...eventArgs) {
    const callback = this.#eventListeners.get(eventType);
    if (callback) {
      // unwrap varargs and call callback
      callback.apply(null, eventArgs);
    }
  }

  onConnectionError(reason) {
    console.error(`connection error: ${reason}`);
    this.disconnect(reason);
  }

  onSessionEvent(event_name, ...event_args) {
    console.debug(`session event: ${event_name}`);
    this.emit(event_name, ...event_args);
    // emitted after participantTrackAdded so consumers attach the stream before reacting to readiness
    if (event_name === SessionEvents.participantTrackAdded && event_args[0]?.isPatient) {
      this.#devicePatientTrackReceived = true;
      this.#maybeEmitPatientMediaReady();
    }
  }

  /**
   * Emits patientMediaReady exactly once, when the patient (device) track has been received AND
   * the device peer connection is actually connected. The track event alone fires during SDP
   * negotiation, before ICE/DTLS completes, so neither signal is sufficient by itself and either
   * can happen first. Consumers should flip their "connected" UI on this event.
   */
  #maybeEmitPatientMediaReady() {
    if (this.#patientMediaReadyEmitted || !this.#deviceConnected || !this.#devicePatientTrackReceived) {
      return;
    }
    this.#patientMediaReadyEmitted = true;
    this.emit(SessionEvents.patientMediaReady, this.#videoDeviceParticipant);
  }

  /**
   * Mutes the local audio (outgoing) stream. Any incoming audio is uneffected.
   */
  muteLocalAudio() {
    this.setLocalAudioEnabled(false);
  }

  /**
   * Unmutes the local audio (outgoing) stream. Any incoming audio is uneffected.
   */
  unmuteLocalAudio() {
    this.setLocalAudioEnabled(true);
  }

  setLocalAudioEnabled(isEnabled) {
    if (this.context?.localMediaStream) {
      this.context.localMediaStream.getAudioTracks().forEach((trk) => {
        trk.enabled = isEnabled;
      });
    }
  }

  /*
   * Invite a browser peer to create a connection with this browser peer. This is used in
   * the meeting context where multiple users can connect to a video device.
   */
  async inviteBrowserPeerParticipant(peerId, displayName = null) {
    if (this.#peerBrowserParticipants.has(peerId)) {
      console.error(`already have an existing connection to ${peerId}`);
      return null;
    }
    console.log(`generating offer for peer user ${peerId}`);
    // same STUN + TURN config as the device and answering-peer connections, so the offering side can relay too
    const rtcPeerConnection = new RTCPeerConnection(this.#deviceRtcPeerConnectionConfig);
    const browserParticipant = new Participant({
      peerId,
      context: this.context,
      rtcPeerConnection,
      participantType: ParticipantTypes.otherBrowserPeer,
      displayName: displayName || this.context.options.userDisplayName,
    })
    this.#peerBrowserParticipants.set(peerId, browserParticipant);

    browserParticipant.on("disconnect", () => {
      console.debug(`peer ${peerId} disconnected`);
      this.#peerBrowserParticipants.delete(peerId);
      this.emit(SessionEvents.participantDisconnected, browserParticipant);
    })
    browserParticipant.on("session-event", this.onSessionEvent.bind(this))

    const iceCandidatesPromise = browserParticipant.collectIceCandidates();
    await browserParticipant.initializePeerConnection();
    if (this.context.screenShareMediaStream) {
      console.debug("adding screen share stream to browser peer participant");
      this.context.screenShareMediaStream.getTracks().forEach((track) => {
        browserParticipant.screenShareTrackSenders.push(
          browserParticipant.rtcPeerConnection.addTrack(track, this.context.screenShareMediaStream)
        );
      });
    }
    console.info("calling setLocalDescription for browser peer offer...")
    await rtcPeerConnection.setLocalDescription();
    const descr = rtcPeerConnection.localDescription;
    console.info("local description set for browser peer offer", descr);
    const localIceCandidates = await iceCandidatesPromise;
    const offerPayload = {
      peer_id: peerId,
      sender_peer_id: this.peerId,
      sender_display_name: this.context.options.userDisplayName,
      type: 'peer-sdp-offer',
      sdp: {
        type: 'offer',
        sdp: descr.sdp
      },
      ice_candidates: localIceCandidates,
      screen_share_stream_id: this.context.screenShareMediaStream?.id || null,
    };
    if (!this.#meetingChannel) {
      // signal through the video device data channel
      this.#videoDeviceParticipant.dataChannel.send(JSON.stringify(offerPayload));
    } else {
      console.debug(`triggering peer sdp offer for ${peerId} through meeting channel`, offerPayload);
      try {
        const compressed = await compressPusherPayload(offerPayload);
        this.#meetingChannel.trigger(`client-peer-sdp-offer@${peerId}`, compressed);
        console.info(`sent peer offer to ${peerId} through meeting channel`);
      } catch (err) {
        console.error(`error triggering peer sdp offer for ${peerId}`, err);
      }
    }
    console.info(`sent peer offer to ${peerId}`);
  }

  async onPeerAnswer(remoteParticipantId, remoteSdp, remoteIceCandidates, senderDisplayName) {
    const participant = this.#peerBrowserParticipants.get(remoteParticipantId);
    if (!participant) {
      console.error(`no remote participant found for ${remoteParticipantId}`);
    }
    if (senderDisplayName) {
      participant.displayName = senderDisplayName;
    }
    await applyRemoteAnswer(participant.rtcPeerConnection, remoteSdp, remoteIceCandidates);
  }

  /**
   * Handler for a browser peer offer. This may be a device kiosk browser or another browser peer.
   */
  async onPeerOffer(remoteParticipantId, remoteSdp, remoteIceCandidates, mediaRequest, senderDisplayName, isKiosk, screenShareStreamId = null) {
    let customLocalMediaStream = null;
    console.info(`received peer offer from ${remoteParticipantId} (isKiosk=${isKiosk})`, mediaRequest);

    const rtcPeerConnection = new RTCPeerConnection(this.#deviceRtcPeerConnectionConfig);
    const participant = new Participant({
      peerId: remoteParticipantId,
      context: this.context,
      rtcPeerConnection,
      participantType: isKiosk ? ParticipantTypes.kiosk : ParticipantTypes.otherBrowserPeer,
      displayName: senderDisplayName,
    })
    participant.on("session-event", this.onSessionEvent.bind(this))
    participant.on("disconnect", () => {
      console.debug(`peer ${remoteParticipantId} disconnected`);
      this.#peerBrowserParticipants.delete(remoteParticipantId);
      this.emit(SessionEvents.participantDisconnected, participant);
    })

    if (screenShareStreamId) {
      console.debug(`setting screen share stream id ${screenShareStreamId} for peer ${remoteParticipantId}`);
      participant.screenShareMediaStreamId = screenShareStreamId;
    }

    if (mediaRequest) {
      if (!isKiosk) {
        console.warn("unexpected media request in peer offer for non-kiosk peer", mediaRequest);
      }
      console.debug(`got media request from peer ${remoteParticipantId}`, mediaRequest);
      // assuming this is for kiosk viewer only for now - need a new stream with different constraints
      // kiosk only needs video
      // Create a new stream with only video tracks from the existing stream
      const mediaStream = new MediaStream();
      if (this.context.localMediaStream.getVideoTracks().length === 0 || mediaRequest.video === false) {
        console.debug("video not requested or user has not allowed video");
      } else {
        this.context.localMediaStream.getVideoTracks().forEach(track => {
          mediaStream.addTrack(track);
        });
      }
      customLocalMediaStream = mediaStream;
      console.debug("created custom local media stream for kiosk", customLocalMediaStream);
      // }
    }

    const iceCandidatesPromise = participant.collectIceCandidates();
    await participant.configureRemote(remoteSdp, remoteIceCandidates, customLocalMediaStream);

    console.debug(`creating answer for ${remoteParticipantId}...`);
    // add santity check to avoid timiing issues when not awaiting async functions :(
    if (rtcPeerConnection.signalingState !== 'have-remote-offer') {
      console.error(`unexpected signaling state ${rtcPeerConnection.signalingState} for ${remoteParticipantId}`);
    }

    await rtcPeerConnection.setLocalDescription();
    const localSdpAnswer = rtcPeerConnection.localDescription;
    const localIceCandidates = await iceCandidatesPromise;
    // relay the peer answer through the device data channel
    const payload = {
      type: 'peer-sdp-answer',
      sender_display_name: this.context.options.userDisplayName,
      peer_id: remoteParticipantId,
      sender_peer_id: this.peerId,
      sdp: localSdpAnswer,
      ice_candidates: localIceCandidates,
    };
    if (!this.#meetingChannel) {
      this.#videoDeviceParticipant.dataChannel.send(JSON.stringify(payload));
    } else {
      console.info("triggering peer sdp answer", remoteParticipantId)
      const compressed = await compressPusherPayload(payload);
      this.#meetingChannel.trigger(`client-peer-sdp-answer@${remoteParticipantId}`, compressed);
    }
    console.info(`sent peer sdp answer to ${remoteParticipantId}`);

    // const localIceCandidates = [];
    // rtcPeerConnection.onicecandidate = (event) => {
    //   if (event.candidate == null) {
    //     // with trickle ICE, a null indicates there are no more ICE candidates
    //     console.debug("ICE Candidate was null, ICE processing is done", localIceCandidates);
    //     // relay the peer answer through the device data channel
    //     const payload = {
    //       type: 'peer-sdp-answer',
    //       peer_id: remoteParticipantId,
    //       sender_peer_id: this.peerId,
    //       sdp: localSdpAnswer,
    //       ice_candidates: localIceCandidates,
    //     };
    //     this.#videoDeviceParticipant.dataChannel.send(JSON.stringify(payload));
    //     console.info(`sent peer sdp answer to ${remoteParticipantId}`);
    //     return;
    //   }
    //   localIceCandidates.push(event.candidate);
    // };

    if (isKiosk) {
      this.#videoDeviceKioskParticipant = participant;
    } else {
      this.#peerBrowserParticipants.set(remoteParticipantId, participant);
    }
  }

  isDeviceDataChannelOpen() {
    return this.#videoDeviceParticipant?.dataChannel?.readyState === 'open';
  }

  isKioskDataChannelOpen() {
    return this.#videoDeviceKioskParticipant?.dataChannel?.readyState === 'open';
  }

  sendDeviceDataChannelMessage(msg) {
    if (this.#videoDeviceParticipant?.dataChannel?.readyState === 'open') {
      console.info("sending device data channel message", msg, this.#videoDeviceParticipant)
      this.#videoDeviceParticipant.dataChannel.send(JSON.stringify(msg));
    } else {
      console.warn(`device data channel is not open. state=${this.#videoDeviceParticipant?.dataChannel?.readyState}`, msg);
      // TODO: a better user experience may be to not display PTZ controls until data channel is open
      // throw new Error('Device data channel is not open. Cannot send message.');
    }
  }

  sendKioskDataChannelMessage(msg) {
    if (this.#videoDeviceKioskParticipant?.dataChannel?.readyState === 'open') {
      console.info("sending device data channel message", msg, this.#videoDeviceKioskParticipant)
      this.#videoDeviceKioskParticipant.dataChannel.send(JSON.stringify(msg));
    } else {
      console.warn(`device data channel is not open. state=${this.#videoDeviceKioskParticipant?.dataChannel?.readyState}`, msg);
      // throw new Error('Device data channel is not open. Cannot send message.');
    }
  }

  async onVideoDeviceDataChannelMessage(payload) {
    // this is the legacy (basic_mode) mechanism for p2p communication
    switch (payload.type) {
      case "generate-offer":
        await this.inviteBrowserPeerParticipant(payload.peer_id);
        break;
      case "peer-sdp-offer":
        await this.onPeerOffer(
          payload.sender_peer_id,
          payload.sdp,
          payload.ice_candidates,
          payload.media_request,
          payload.sender_display_name,
          payload.sender_peer_id === "kiosk-viewer",
          payload.screen_share_stream_id);
        break;
      case "peer-sdp-answer":
        await this.onPeerAnswer(payload.sender_peer_id, payload.sdp, payload.ice_candidates, payload.sender_display_name);
        break;
      case "ai-nudge":
        console.info("received ai-nudge", payload);
        this.emit(SessionEvents.nudgeReceived, payload);
        break;
      case "user-bye":
        // assuming connection/track disconnection logic can handle this
        console.info(`received user-bye from ${payload.sender_peer_id}`);
        break;
      default:
        console.error(`unexpected data channel payload type: ${payload.type}`);
    }
  }

  enableWebrtcWatcher(peerConn) {
    const qualityIntervalMs = this.context.options.webrtcQualityEnabled ? 2000 : 0;
    const statsIntervalMs = this.context.options.statsIntervalMs || 0;
    if (qualityIntervalMs > 0 || statsIntervalMs > 0) {
      this.#webrtcWatcher = new WebRTCConnectionWatcher({
        connection: peerConn,
        qualityIntervalMs,
        statsIntervalMs,
      })
      this.#webrtcWatcher.on("quality-updated", (data) => {
        this.emit(SessionEvents.webrtcQualityUpdated, data);
      });
      this.#webrtcWatcher.on("issues", (data) => {
        this.emit(SessionEvents.webrtcIssue, data);
      });
      this.#webrtcWatcher.on("scores-updated", (data) => {
        this.emit(SessionEvents.webrtcNetworkScoreUpdated, data);
      });
      this.#webrtcWatcher.start()
      console.debug("webrtc watcher enabled", qualityIntervalMs, statsIntervalMs);
    } else {
      console.debug("webrtc watcher not enabled");
    }
  }



  /**
   * Starts a session with a connection to the video device.
   * 
   * @param {JoinData} joinData - the join data from the server
   * @param {SessionTypes} type of session (meeting | monitoring | test)
   * @param {MediaStream} localMediaStream - the local media stream
   * @param {SessionContextOptions} options - the session options
   * @returns {Promise<void>}
   */
  async connect(joinData, sessionType, localMediaStream, options = {}) {
    // guard against double-connect: a second connect() without a disconnect would
    // overwrite #videoDeviceParticipant and orphan the previous (open) RTCPeerConnection,
    // which is never closed and never garbage collected (counts against Chrome's 500 cap)
    if (this.#videoDeviceParticipant) {
      console.warn('WebRTCSession.connect called while a session is already active - disconnecting previous first');
      await this.disconnect();
    }
    this.#deviceConnected = false;
    this.#devicePatientTrackReceived = false;
    this.#patientMediaReadyEmitted = false;
    this.context = new SessionContext({
      joinData,
      sessionType,
      localMediaStream,
      screenShareMediaStream: null,
      options,
    });
    this.peerId = joinData.meeting_peer_id || joinData.peer_id;
    this.#deviceRtcPeerConnectionConfig = buildRTCPeerConnectionConfig(
      this.context.options.rtcPeerConnectionConfig,
      joinData.turn_servers
    )

    // connect to video device
    const devicePeerConn = new RTCPeerConnection(this.#deviceRtcPeerConnectionConfig);

    // viewer-side black-box telemetry — passive, fail-safe, must never affect the call
    const telemetry = options.telemetry
    if (telemetry?.diagnosticsEnabled) {
      try {
        const videoDeviceId = typeof joinData.device_id === 'number' ? joinData.device_id : joinData.device_id.split('-').slice(-1)[0]
        // Match the raw numeric user.id the device records as peer_id, so the viewer record joins to
        // the device bundle. meeting_peer_id ("user-<id>") is only set for non-basic meetings;
        // monitoring/test/basic-mode sessions carry the id as the raw joinData.peer_id instead.
        // this.peerId already resolves to (meeting_peer_id || peer_id) above, so derive from it.
        const rawPeerId = String(this.peerId || '').replace(/^user-/, '')
        this.#viewerTelemetry = new ViewerTelemetry(devicePeerConn, {
          session_id: telemetry.sessionId,
          telemetry_id: telemetry.telemetryId,
          device_id: `video-device-${videoDeviceId}`,
          peer_id: /^\d+$/.test(rawPeerId) ? Number(rawPeerId) : (rawPeerId || null),
          channel: sessionType,
          camera: telemetry.camera,
          app_version: telemetry.appVersion,
          emit: telemetry.emit,
        })
      } catch (err) {
        console.warn("[ViewerTelemetry] failed to start viewer telemetry", err)
      }
    } else {
      console.debug("[ViewerTelemetry] disabled — Video Diagnostics is off for this device")
    }

    const deviceParticipant = new VideoDeviceParticipant({
      peerId: joinData.device_id,
      rtcPeerConnection: devicePeerConn,
      context: this.context,
      videoDeviceId: typeof joinData.device_id === 'number' ? joinData.device_id : joinData.device_id.split('-').slice(-1)[0],  // device_id is in the format "video_device-<id>"
      dataChannelMessageListener: this.onVideoDeviceDataChannelMessage.bind(this),
      sendVideo: sessionType === SessionTypes.meeting && joinData.device_kind === DeviceKinds.kiosk,
    })
    this.#videoDeviceParticipant = deviceParticipant
    deviceParticipant.on("error", this.onConnectionError.bind(this))
    deviceParticipant.on("session-event", this.onSessionEvent.bind(this))

    // deviceParticipant.on("device-datachannel-message", this.onVideoDeviceDataChannelMessage.bind(this))

    const markDeviceConnected = () => {
      if (this.#deviceConnected) {
        return;
      }
      this.#deviceConnected = true;
      this.enableWebrtcWatcher(devicePeerConn);
      this.emit(SessionEvents.connectSucceeded, this);
      this.#maybeEmitPatientMediaReady();
    }
    devicePeerConn.addEventListener('connectionstatechange', () => {
      console.debug(`${this.peerId} connection state changed to ${devicePeerConn.connectionState}`);
      if (devicePeerConn.connectionState === 'connected') {
        markDeviceConnected();
      } else if (devicePeerConn.connectionState === 'disconnected' || devicePeerConn.connectionState === 'failed') {
        // this won't be the state if the connection is closed normally (connectionState will be 'closed')
        this.emit(SessionEvents.participantDisconnected, deviceParticipant);
        this.disconnect();
      }
    })
    devicePeerConn.addEventListener('iceconnectionstatechange', () => {
      // fallback for browsers without RTCPeerConnection.connectionState (e.g. Firefox < 113),
      // where connectionstatechange never fires; when connectionState exists it is authoritative
      if (devicePeerConn.connectionState !== undefined) {
        return;
      }
      if (devicePeerConn.iceConnectionState === 'connected' || devicePeerConn.iceConnectionState === 'completed') {
        markDeviceConnected();
      }
    })
    await deviceParticipant.configureRemote(joinData.sdp, joinData.ice_candidates);
    console.debug("remote sdp set. creating answer...");
    if (devicePeerConn.signalingState !== 'have-remote-offer') {
      console.error(`unexpected signaling state ${devicePeerConn.signalingState}`, deviceParticipant);
    }
    let ice_candidates_batch = [];
    devicePeerConn.addEventListener("icecandidate", (event) => {
      if (event.candidate != null) {
        console.debug("local ICE Candidate", event.candidate);
        if (ice_candidates_batch) {
          ice_candidates_batch.push(event.candidate);
          if (ice_candidates_batch.length === 1) {
            // typically the first 2-3 ICE candidates are cached and returned immediately one after another
            // so wait a tiny bit before sending the initial batch
            setTimeout(() => {
              this.emit(SessionEvents.signalRequired, {
                video_type: sessionType,
                signal_type: 'ice-candidates',
                ice_candidates: ice_candidates_batch,
              });
              ice_candidates_batch = null;
            }, 40);
          }
        } else {
          this.emit(SessionEvents.signalRequired, {
            video_type: sessionType,
            signal_type: 'ice-candidates',
            ice_candidates: [event.candidate],
          });
        }
      } else {
        // with trickle ICE, a null indicates there are no more ICE candidates
        console.debug("local ICE Candidate was null, ICE processing is done");
      }
    });
    await devicePeerConn.setLocalDescription();
    const localSdpAnswer = devicePeerConn.localDescription;
    // we don't have a connection to the device yet so we need to signal through the server
    this.emit(SessionEvents.signalRequired, {
      video_type: sessionType,
      signal_type: 'sdp-answer',
      sdp: localSdpAnswer,
    });
  }

  async disconnect(disconnectReason = null) {
    console.debug(`starting disconnect of ${this.peerId}...`);
    if (this.#webrtcWatcher) {
      this.#webrtcWatcher.stop();
      this.#webrtcWatcher = null;
    }
    if (this.#viewerTelemetry) {
      this.#viewerTelemetry.finish();
      this.#viewerTelemetry = null;
    }
    const selfId = this.context?.joinData?.peer_id;
    if (this.#meetingChannel == null) {
      // without a presence channel, contact each peer directly to notify of disconnection
      this.#peerBrowserParticipants.forEach((participant) => {
        participant.disconnect(disconnectReason);
        if (this.#videoDeviceParticipant.dataChannel) {
          try {
            this.#videoDeviceParticipant.dataChannel.send(JSON.stringify({
              peer_id: selfId,  // send the peer id of the participant that is disconnecting
              type: 'user-bye'
            }));
            console.debug(`sent user-bye from ${selfId} to ${participant.peerId}`);
          } catch (err) {
            console.warn(`error sending user-bye to ${participant.peerId}`, err);
          }
        } else {
          console.warn(`no video device data channel to send user-bye to ${participant.peerId}`);
        }
      })
    } // in pusher mode, expecting WebrtcView to call leaveMeetingRoom()
    this.#peerBrowserParticipants.clear();
    if (this.#videoDeviceKioskParticipant) {
      this.#videoDeviceKioskParticipant.disconnect();
      this.#videoDeviceKioskParticipant = null;
    }
    // this.#screenShareMediaStream = null;
    // video device participant should be closed last in case data channel messages are sent during disconnect
    if (this.#videoDeviceParticipant) {
      this.#videoDeviceParticipant.disconnect();
      this.#videoDeviceParticipant = null;
    }

    try {
      this.emit(SessionEvents.disconnected, disconnectReason);
    } finally {
      this.#eventListeners.clear();
    }
  }

  onPusherMemberAdded(member) {
    if (member.id !== this.peerId) {
      console.info(`member ${member.id} joined meeting room`, member);
      if (member.info.type !== "device-kiosk") {
        // by convention, when new members join, existing members initiate connections to them
        this.inviteBrowserPeerParticipant(member.id, member.info.name)
      }
    } else {
      console.debug('ignoring member added for self')
    }
  }

  onPusherMemberRemoved(member) {
    if (member.id !== this.peerId) {
      console.info(`member ${member.id} left meeting room`, member);
      const participant = this.#peerBrowserParticipants.get(member.id);
      if (participant) {
        console.debug(`cleaning up participant for removed member ${member.id}`);
        participant.disconnect();
        this.#peerBrowserParticipants.delete(member.id);
        this.emit(SessionEvents.participantDisconnected, participant);
      }
    } else {
      console.debug('ignoring member removed for self')
    }
  }

  async onPusherClientEvent(eventName, data) {
    if (!isEventTargetedAtPeer(eventName, this.peerId)) return;
    data = await decompressPusherPayload(data);
    if (eventName.startsWith('client-kiosk-offer@')) {
      console.info(`received kiosk offer from ${data.sender_peer_id}`, data);
      this.onPeerOffer(
        data.sender_peer_id,
        data.sdp,
        data.ice_candidates,
        data.media_request,
        data.sender_display_name,
        true);
    } else if (eventName.startsWith('client-peer-sdp-offer@')) {
      console.info(`received peer offer from ${data.sender_peer_id}`, data);
      this.onPeerOffer(
        data.sender_peer_id,
        data.sdp,
        data.ice_candidates,
        data.media_request,
        data.sender_display_name,
        false,
        data.screen_share_stream_id);
    } else if (eventName.startsWith('client-peer-sdp-answer@')) {
      console.info(`received peer answer from ${data.sender_peer_id}`, data);
      this.onPeerAnswer(
        data.sender_peer_id,
        data.sdp,
        data.ice_candidates,
        data.sender_display_name);
    } else if (eventName === "client-screen-share-start" && data.sender_peer_id !== this.peerId) {
      console.info(`received screen share stream from ${data.sender_peer_id}`, data);
      // TODO: emit screen share stream
      // someone started screen sharing. initiate a connection
      // const screenSharePeerId = data.screen_share_peer_id;

    } else if (eventName === "client-screen-share-stop" && data.sender_peer_id !== this.peerId) {
      console.info(`received screen share stop from ${data.sender_peer_id}`, data);
      // TODO: handle screen share stop
    } else {
      console.warn("unexpected pusher client event", eventName, data);
    }
  }

  leaveMeetingRoom(pusher) {
    if (this.#meetingChannel) {
      console.info(`leaving meeting room ${this.#meetingChannel.name}...`);
      pusher.unsubscribe(this.#meetingChannel.name);
      this.#meetingChannel.unbind_all()
      this.#meetingChannel = null;
      console.info("left meeting room");
    }
  }

  enterMeetingRoom(pusher) {
    const channelName = this.context.joinData.meeting_channel;
    console.info(`entering meeting room ${channelName}...`);
    this.#meetingChannel = pusher.subscribe(channelName);

    this.#meetingChannel.bind('pusher:subscription_succeeded', (members) => {
      console.info(`subscribed to meeting room ${channelName} successfully`, members);
      // this.peerId = this.#meetingChannel.members.myID;
    });
    this.#meetingChannel.bind('pusher:member_added', this.onPusherMemberAdded.bind(this));
    this.#meetingChannel.bind('pusher:member_removed', this.onPusherMemberRemoved.bind(this));

    // Bind to all client events (events prefixed with "client-")
    this.#meetingChannel.bind_global((eventName, data) => {
      if (eventName.startsWith('client-')) {
        this.onPusherClientEvent(eventName, data);
      }
    });

    // don't seem to get members immediately here. use subscription_succeeded instead
    console.info("entered meeting room.");
  }

  activateScreenShare(screenShareStream) {
    console.info("activating screen share", screenShareStream);
    this.context.screenShareMediaStream = screenShareStream;
    // Tear down the whole share only when the *video* track ends (e.g. the user clicks the
    // browser's "Stop sharing"). A screen-share audio track may end independently (e.g. the
    // user mutes the tab) and must not stop the share.
    const videoTrack = screenShareStream.getVideoTracks()[0];
    if (videoTrack) {
      videoTrack.onended = () => {
        console.info("screen share video track ended");
        this.deactivateScreenShare(screenShareStream);
      }
    }
    if (this.#videoDeviceKioskParticipant) {
      this.#addScreenShareToParticipant(this.#videoDeviceKioskParticipant, screenShareStream);
    }
    this.#peerBrowserParticipants.forEach((participant) => {
      this.#addScreenShareToParticipant(participant, screenShareStream);
    });
  }

  /**
   * Adds all screen-share tracks (video + optional audio) to a participant once its
   * connection is stable and its data channel is open, to avoid negotiation glare and
   * dropped offers. Every resulting sender is tracked so it can be cleanly removed later.
   */
  #addScreenShareToParticipant(participant, screenShareStream) {
    this.#whenParticipantReadyForRenegotiation(participant, () => {
      // bail if the share was stopped (or replaced) while we were waiting to become ready
      if (this.context.screenShareMediaStream !== screenShareStream) {
        console.debug(`screen share no longer active; skipping add for ${participant.peerId}`);
        return;
      }
      console.debug(`adding screen share to ${participant.peerId}`, screenShareStream.getTracks());
      try {
        // announce the stream id first (reliable, ordered) so the receiver classifies the
        // renegotiated track(s) as screen share rather than a normal participant stream
        participant.dataChannel.send(JSON.stringify({
          type: 'screen-share-start',
          fromPeerId: this.peerId,
          screenShareMediaStreamId: screenShareStream.id,
        }));
      } catch (err) {
        console.warn(`error sending screen-share-start to ${participant.peerId}`, err);
      }
      screenShareStream.getTracks().forEach((track) => {
        participant.screenShareTrackSenders.push(
          participant.rtcPeerConnection.addTrack(track, screenShareStream)
        );
      });
    });
  }

  /**
   * Runs callback once the participant's connection is stable and its data channel is open
   * (so the renegotiation offer triggered by addTrack can actually be sent). Runs
   * immediately if already ready; otherwise defers until both conditions are met.
   */
  #whenParticipantReadyForRenegotiation(participant, callback) {
    const pc = participant.rtcPeerConnection;
    const isReady = () =>
      pc && pc.signalingState === 'stable' && participant.dataChannel?.readyState === 'open';
    if (isReady()) {
      callback();
      return;
    }
    console.debug(`participant ${participant.peerId} not ready for renegotiation; deferring screen share`);
    let settled = false;
    const cleanup = () => {
      settled = true;
      pc?.removeEventListener('signalingstatechange', onChange);
      participant.dataChannel?.removeEventListener('open', onChange);
    };
    const onChange = () => {
      if (!settled && isReady()) {
        cleanup();
        callback();
      }
    };
    pc?.addEventListener('signalingstatechange', onChange);
    participant.dataChannel?.addEventListener('open', onChange);
    // Safety: stop waiting (and release the listeners + closure) if the peer never becomes ready,
    // e.g. it disconnects mid-negotiation. close() does not fire signalingstatechange, so without
    // this the listeners would linger on the dead connection.
    setTimeout(() => {
      if (!settled) {
        console.warn(`screen share renegotiation defer timed out for ${participant.peerId}`);
        cleanup();
      }
    }, 15000);
  }

  deactivateScreenShare(screenShareStream) {
    console.info("deactivating screen share", screenShareStream);
    this.context.screenShareMediaStream = null;
    if (this.#videoDeviceKioskParticipant) {
      this.#removeScreenShareFromParticipant(this.#videoDeviceKioskParticipant);
    }
    this.#peerBrowserParticipants.forEach((participant) => {
      this.#removeScreenShareFromParticipant(participant);
    });
  }

  /**
   * Removes all tracked screen-share senders from a participant. Each removeTrack is guarded
   * so a closed connection or already-removed sender can't throw and abort cleanup of the
   * remaining senders / participants.
   */
  #removeScreenShareFromParticipant(participant) {
    const senders = participant.screenShareTrackSenders;
    if (!senders || senders.length === 0) {
      return;
    }
    console.debug(`removing screen share from ${participant.peerId}`);
    if (participant.dataChannel?.readyState === 'open') {
      try {
        participant.dataChannel.send(JSON.stringify({
          type: 'screen-share-stop',
          fromPeerId: this.peerId,
        }));
      } catch (err) {
        console.warn(`error sending screen-share-stop to ${participant.peerId}`, err);
      }
    }
    const pc = participant.rtcPeerConnection;
    senders.forEach((sender) => {
      if (sender && pc && pc.signalingState !== 'closed') {
        try {
          pc.removeTrack(sender);
        } catch (err) {
          console.warn(`error removing screen share track from ${participant.peerId}`, err);
        }
      }
    });
    participant.screenShareTrackSenders = [];
  }


  // TODO: Aimee participant in meetings
  // - use a separate aimee agent that has acces to this session
  // - for Aimee audio going to video device, send request to video device to add a recvonly audio transceiver;
  // device sends new offer with new transceiver. connect the aimee track to the new transceiver and send answer;
  // this can be done with the device's data channel for signaling;
  // NOTE: since the device initiated the connection, it needs to add the transceiver and send a new offer
  // - for audio going to Aimee, use web audio api to connect all audio tracks to a single destination track
  // that is added to the Aimee RTCPeerConnection
}